import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Button, List, Typography, Space, Pagination, Spin, App } from 'antd';
import { SearchOutlined, CloseOutlined, CheckOutlined, PlusOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { message } from '../utils/message';

const { Text } = Typography;

// 네이버 지도 API 타입 선언
declare global {
  interface Window {
    naver: any;
    navermap_authFailure?: () => void;
  }
}

interface MapSearchResult {
  id: string;
  name: string;
  address: string;
  roadAddress: string;
  x: number; // 경도
  y: number; // 위도
  category?: string;
  telephone?: string;
  walkingDistanceMeters?: number; // 도보 거리 (미터)
  walkingDurationMinutes?: number; // 도보 시간 (분)
}

interface MapSearchModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (result: MapSearchResult) => void;
}

interface Place {
  name: string;
  address: string;
  roadAddress: string;
  lat: number;
  lon: number;
  category: string;
  phone: string;
}

interface SearchResponse {
  success: boolean;
  places: Place[];
  total: number;
  page: number;
  display: number;
  hasMore: boolean;
  query: string;
  timestamp: string;
}

const MapSearchModal: React.FC<MapSearchModalProps> = ({ visible, onClose, onSelect }) => {
  const { message } = App.useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<MapSearchResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<MapSearchResult | null>(null);
  const [isSearched, setIsSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lon: number }>({
    lat: 37.5665, // 서울 시청 기본 위치
    lon: 126.9780
  });

  const mapRef = useRef<HTMLDivElement>(null);
  const naverMapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const selectedMarkerRef = useRef<any>(null);

  const pageSize = 10;

  // 네이버 지도 API 인증 실패 처리
  useEffect(() => {
    window.navermap_authFailure = () => {
      console.error('네이버 지도 API 인증에 실패했습니다.');
      message.error('지도 서비스를 사용할 수 없습니다. 네이버 지도 API 인증을 확인해주세요.');
    };

    return () => {
      delete window.navermap_authFailure;
    };
  }, [message]);

  // 현재 위치 가져오기 (모달이 열렸을 때만 실행)
  useEffect(() => {
    if (visible && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
        },
        (error) => {
          console.warn('위치 정보를 가져올 수 없습니다:', error);
          // 기본 위치 유지
        }
      );
    }
  }, [visible]);

  // 네이버 지도 API 로드 및 초기화
  useEffect(() => {
    if (visible) {
      loadNaverMapScript();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (visible && mapRef.current && window.naver) {
      initializeMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, currentLocation]);

  const loadNaverMapScript = () => {
    // 이미 스크립트가 로드되어 있으면 지도 초기화
    if (window.naver && window.naver.maps) {
      if (mapRef.current) {
        initializeMap();
      }
      return;
    }

    // 스크립트가 이미 추가되어 있는지 확인
    const existingScript = document.getElementById('naver-map-script');
    if (existingScript) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'naver-map-script';
    script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${process.env.REACT_APP_NAVER_MAP_CLIENT_ID}`;
    script.async = true;
    script.onload = () => {
      if (mapRef.current) {
        initializeMap();
      }
    };
    script.onerror = () => {
      console.error('네이버 지도 API 로드 실패');
      message.error('지도를 로드할 수 없습니다.');
    };
    document.head.appendChild(script);
  };

  const initializeMap = () => {
    if (!mapRef.current || !window.naver) return;

    const mapOptions = {
      center: new window.naver.maps.LatLng(currentLocation.lat, currentLocation.lon),
      zoom: 15,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.naver.maps.MapTypeControlStyle.BUTTON,
        position: window.naver.maps.Position.TOP_RIGHT
      },
      zoomControl: true,
      zoomControlOptions: {
        style: window.naver.maps.ZoomControlStyle.SMALL,
        position: window.naver.maps.Position.TOP_RIGHT
      }
    };

    naverMapRef.current = new window.naver.maps.Map(mapRef.current, mapOptions);

    // 지도 클릭 이벤트 (역지오코딩)
    window.naver.maps.Event.addListener(naverMapRef.current, 'click', handleMapClick);
  };

  const handleMapClick = async (e: any) => {
    const lat = e.coord.y;
    const lon = e.coord.x;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/search/reverse-geocoding?lat=${lat}&lon=${lon}`);
      const data = await response.json();

      if (data.success && data.data.results && data.data.results.length > 0) {
        const result = data.data.results[0];
        const location = result.region || result.land || {};

        const mapResult: MapSearchResult = {
          id: `click_${Date.now()}`,
          name: location.name || '선택한 위치',
          address: data.data.address || `위도: ${lat.toFixed(6)}, 경도: ${lon.toFixed(6)}`,
          roadAddress: data.data.roadAddress || '',
          x: lon,
          y: lat
        };

        setSelectedResult(mapResult);
        addMarkerToMap(mapResult, true);
      }
    } catch (error) {
      console.error('역지오코딩 실패:', error);
      message.error('위치 정보를 가져올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        query: searchTerm,
        lat: currentLocation.lat.toString(),
        lon: currentLocation.lon.toString(),
        page: currentPage.toString(),
        display: pageSize.toString(),
        sort: 'comment'
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/search/places?${params}`);
      const data: SearchResponse = await response.json();

      if (data.success && data.places) {
        const results: MapSearchResult[] = data.places.map((item: Place, index: number) => ({
          id: `search_${currentPage}_${index}`,
          name: item.name || '', // 백엔드에서 이미 HTML 태그 제거됨
          address: item.address,
          roadAddress: item.roadAddress,
          x: item.lon, // 백엔드에서 이미 변환된 좌표
          y: item.lat,
          category: item.category,
          telephone: item.phone
        }));

        setSearchResults(results);
        setTotalResults(data.total);
        setIsSearched(true);
        setSelectedResult(null);

        // 지도에 마커 추가
        clearMarkers();
        results.forEach(result => addMarkerToMap(result, false));

        // 첫 번째 결과 위치로 지도 이동
        if (results.length > 0 && naverMapRef.current) {
          const firstResult = results[0];
          naverMapRef.current.setCenter(new window.naver.maps.LatLng(firstResult.y, firstResult.x));
        }
      } else {
        message.error('검색 결과가 없습니다.');
        setSearchResults([]);
        setTotalResults(0);
        setIsSearched(true);
      }
    } catch (error) {
      console.error('검색 실패:', error);
      message.error('검색 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const addMarkerToMap = (result: MapSearchResult, isSelected: boolean) => {
    if (!naverMapRef.current || !window.naver) return;

    const position = new window.naver.maps.LatLng(result.y, result.x);

    const marker = new window.naver.maps.Marker({
      position: position,
      map: naverMapRef.current,
      title: result.name,
      icon: {
        content: isSelected ?
          '<div style="background: #ff4d4f; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>' :
          '<div style="background: #1890ff; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
        anchor: new window.naver.maps.Point(10, 10)
      }
    });

    if (isSelected) {
      if (selectedMarkerRef.current) {
        selectedMarkerRef.current.setMap(null);
      }
      selectedMarkerRef.current = marker;
    } else {
      markersRef.current.push(marker);
    }

    // 마커 클릭 이벤트
    window.naver.maps.Event.addListener(marker, 'click', () => {
      setSelectedResult(result);
      naverMapRef.current.setCenter(position);
    });
  };

  const clearMarkers = () => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    if (selectedMarkerRef.current) {
      selectedMarkerRef.current.setMap(null);
      selectedMarkerRef.current = null;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectLocation = async (result: MapSearchResult) => {
    setLoading(true);

    let walkingDistanceMeters: number | undefined;
    let walkingDurationMinutes: number | undefined;

    try {
      // 도보 거리 및 시간 조회
      // console.log('🚶 도보 거리 조회 시작', { lat: result.y, lon: result.x });
      const startLocationResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/system/start-location`);
      const startLocationData = await startLocationResponse.json();
      // console.log('📍 출발지 위치:', startLocationData);

      if (startLocationData.success) {
        const { lat: startLat, lon: startLon } = startLocationData.data;
        const walkingUrl = `${process.env.REACT_APP_API_BASE_URL}/api/maps/walking-distance?startLat=${startLat}&startLon=${startLon}&endLat=${result.y}&endLon=${result.x}&useCache=false`;
        // console.log('🔗 도보 거리 API 호출:', walkingUrl);

        // 도보 거리 API 호출
        const walkingDistanceResponse = await fetch(walkingUrl);
        const walkingDistanceData = await walkingDistanceResponse.json();
        // console.log('✅ 도보 거리 API 응답:', walkingDistanceData);

        if (walkingDistanceData.success) {
          walkingDistanceMeters = walkingDistanceData.data.distanceMeters;
          walkingDurationMinutes = walkingDistanceData.data.durationMinutes;
          // console.log('✨ 도보 거리 설정:', { walkingDistanceMeters, walkingDurationMinutes });
        } else {
          console.warn('⚠️ 도보 거리 조회 실패:', walkingDistanceData);
        }
      } else {
        console.warn('⚠️ 출발지 조회 실패:', startLocationData);
      }
    } catch (error) {
      console.error('❌ 도보 거리 조회 실패:', error);
      // 에러가 발생해도 선택은 진행
    } finally {
      setLoading(false);
    }

    // 새 객체 생성하여 React 상태 변경 감지 보장
    const updatedResult: MapSearchResult = {
      ...result,
      walkingDistanceMeters,
      walkingDurationMinutes
    };

    console.log('🎯 최종 선택된 결과:', updatedResult);
    setSelectedResult(updatedResult);

    if (naverMapRef.current) {
      const position = new window.naver.maps.LatLng(result.y, result.x);
      naverMapRef.current.setCenter(position);
      addMarkerToMap(updatedResult, true);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // 페이지 변경 시 다시 검색
    setTimeout(() => {
      handleSearch();
    }, 0);
  };

  const handleConfirm = () => {
    if (selectedResult) {
      console.log('✅ MapSearchModal - 확인 버튼 클릭, 선택된 결과:', selectedResult);
      onSelect(selectedResult);
      handleModalClose();
    } else {
      message.warning('위치를 먼저 선택해주세요.');
    }
  };

  const handleModalClose = () => {
    onClose();
    // Reset state
    setSearchTerm('');
    setSearchResults([]);
    setSelectedResult(null);
    setIsSearched(false);
    setCurrentPage(1);
    setTotalResults(0);
    clearMarkers();

    // 부드러운 DOM 정리 - 지도 Modal 관련 요소만 정리
    setTimeout(() => {
      // 지도 Modal 관련 요소만 선택적으로 제거
      const mapModalElements = document.querySelectorAll('.map-search-modal');
      mapModalElements.forEach(element => {
        if (element.parentNode && !element.querySelector('.ant-modal')) {
          // Modal 내용이 없는 빈 래퍼만 제거
          element.parentNode.removeChild(element);
        }
      });

      // body에서 Modal 관련 클래스 제거
      document.body.classList.remove('ant-scrolling-effect');
    }, 300);
  };

  return (
    <Modal
        title="지도 검색"
        open={visible}
        onCancel={handleModalClose}
        footer={null}
        width={1200}
        wrapClassName="map-search-modal"
        destroyOnHidden={true}
        maskClosable={true}
        styles={{
          body: { padding: '24px', height: '600px' },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)'
          }
        }}
        closeIcon={<CloseOutlined style={{ color: '#ffffff' }} />}
      >
        <div style={{ display: 'flex', height: '100%', gap: '24px' }}>
          {/* 좌측 검색 영역 */}
          <div style={{ width: '400px', display: 'flex', flexDirection: 'column' }}>
            {/* 검색 입력 */}
            <div style={{ marginBottom: '24px' }}>
              <Input
                placeholder="장소명을 검색하세요."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                suffix={
                  <Button
                    type="text"
                    icon={loading ? <Spin size="small" /> : <SearchOutlined />}
                    onClick={handleSearch}
                    disabled={loading}
                    style={{ border: 'none', boxShadow: 'none' }}
                  />
                }
                size="large"
              />
            </div>

            {/* 현재 위치 버튼 */}
            {/* <div style={{ marginBottom: '16px' }}>
              <Button
                icon={<EnvironmentOutlined />}
                onClick={() => {
                  if (naverMapRef.current) {
                    naverMapRef.current.setCenter(new window.naver.maps.LatLng(currentLocation.lat, currentLocation.lon));
                  }
                }}
                style={{ width: '100%' }}
              >
                현재 위치로 이동
              </Button>
            </div> */}

            {/* 검색 결과가 없을 때 */}
            {!isSearched && (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#a6a6a6'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}>
                  <SearchOutlined style={{ fontSize: '24px', color: '#a6a6a6' }} />
                </div>
                <Text style={{ color: '#a6a6a6', textAlign: 'center' }}>
                  원하시는 장소를 검색하세요.
                </Text>
              </div>
            )}

            {/* 검색 결과 리스트 */}
            {isSearched && (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '100%', // 최대 높이 제한 제거
                overflow: 'hidden'
              }}>
                {searchResults.length > 0 ? (
                  <>
                    <div style={{
                      flex: 1,
                      overflow: 'auto',
                      maxHeight: 'calc(100vh - 300px)' // 뷰포트 높이 기준으로 동적 계산
                    }}>
                      <List
                        dataSource={searchResults}
                        renderItem={(item) => (
                          <List.Item
                            style={{
                              padding: '16px',
                              cursor: 'pointer',
                              border: selectedResult?.id === item.id ? '2px solid #ff6b35' : '1px solid transparent',
                              borderRadius: '6px',
                              marginBottom: '8px'
                            }}
                            onClick={() => handleSelectLocation(item)}
                          >
                            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                  <Text strong style={{ marginRight: '8px' }}>{item.name}</Text>
                                  {selectedResult?.id === item.id ? (
                                    <Button
                                      type="primary"
                                      size="small"
                                      icon={<CheckOutlined />}
                                      style={{ minWidth: '60px' }}
                                    >
                                      선택됨
                                    </Button>
                                  ) : (
                                    <Button
                                      size="small"
                                      icon={<PlusOutlined />}
                                      style={{ minWidth: '60px' }}
                                    >
                                      선택
                                    </Button>
                                  )}
                                </div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {item.roadAddress || item.address}
                                </Text>
                                {item.category && (
                                  <div style={{ marginTop: '4px' }}>
                                    <Text type="secondary" style={{ fontSize: '11px', color: '#666' }}>
                                      {item.category}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            </div>
                          </List.Item>
                        )}
                      />
                    </div>

                    {/* 페이지네이션 */}
                    {totalResults > pageSize && (
                      <div style={{
                        textAlign: 'center',
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid #f0f0f0'
                      }}>
                        <Pagination
                          current={currentPage}
                          total={totalResults}
                          pageSize={pageSize}
                          showSizeChanger={false}
                          onChange={handlePageChange}
                          simple
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#a6a6a6'
                  }}>
                    <Text style={{ color: '#a6a6a6' }}>검색 결과가 없습니다.</Text>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 우측 지도 영역 */}
          <div style={{
            flex: 1,
            backgroundColor: '#f5f5f5',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            height: 'calc(100% - 60px)', // 하단 여백 확보
            maxHeight: 'calc(100% - 60px)',
            marginBottom: '60px' // 확인 버튼 공간 확보
          }}>
            {/* 네이버 지도 */}
            <div
              ref={mapRef}
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '6px'
              }}
            />

            {/* 선택된 위치 정보 */}
            {selectedResult && (
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                right: '20px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                color: '#333',
                padding: '12px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                maxWidth: 'calc(100% - 40px)', // 좌우 여백 확보
                zIndex: 10 // 지도 위에 표시
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  📍 {selectedResult.name}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  {selectedResult.roadAddress || selectedResult.address}
                </div>
                {selectedResult.category && (
                  <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>
                    {selectedResult.category}
                  </div>
                )}
                {selectedResult.walkingDistanceMeters && selectedResult.walkingDurationMinutes && (
                  <div style={{ fontSize: '12px', color: '#1890ff', marginTop: '8px', fontWeight: '500' }}>
                    🚶 도보 {selectedResult.walkingDurationMinutes}분 ({selectedResult.walkingDistanceMeters}m)
                  </div>
                )}
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  size="small"
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    minWidth: 'auto',
                    width: '20px',
                    height: '20px',
                    padding: 0
                  }}
                  onClick={() => setSelectedResult(null)}
                />
              </div>
            )}

            {/* 로딩 오버레이 */}
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px'
              }}>
                <Spin size="large" />
              </div>
            )}
          </div>
        </div>

        {/* 하단 버튼 */}
        <div style={{
          position: 'absolute',
          bottom: '48px',
          right: '48px',
          zIndex: 1000
        }}>
          <Space>
            {/* <Button size="large" onClick={handleModalClose}>
              취소
            </Button> */}
            <Button
              type="primary"
              size="large"
              onClick={handleConfirm}
              disabled={!selectedResult}
            >
              확인
            </Button>
          </Space>
        </div>
      </Modal>
  );
};

export default MapSearchModal;